// src > UxGrid > Uxgrid
import React from 'react'
import { ThemeProvider } from 'styled-components'
import FlexBox from '../FlexBox'
import Column from '../Column'
import Scale from '../Scale'
import BlockData, { IBlockData, BlockDataStatus } from '../BlockData'
import BlockTypeSelector from '../BlockTypeSelector'
import { IBlockType } from '../BlockType'
// import UxGridFrame from '../UxGridFrame'
import ScalePrototype, { IScaleData } from '../ScaleData'
import { withScroll, UxGridFrameScrollState } from '../UxGridFrame'
import { deepCopy, isEqual } from '../utils/Object'
import { bucketLessAndGreaterThan, difference, indexOf, hasDifference } from '../utils/Array'
import { randomInteger } from '../utils/Number'
import theme from '../theme'
import styles from './UxGrid.module.css'
import {
	QueryStatus,
	UxGridProps as Props,
	UxGridState as State,
	BlockTypeSelectState,
	BlockTypeSelectProps,
	defaultProps
} from './IUxGrid'
import Overlay from '../Overlay'

class UxGrid<BlockTypesType extends object> extends React.Component<
	Props<BlockTypesType> & Partial<UxGridFrameScrollState>,
	State<BlockTypesType>
> {
	private readonly _originalScale: IScaleData = this.props.scale
	// columnIds: { [key: string]: number } = {}
	private _columnIds: number[] = []
	_activeMethod?: Function = undefined
	colRefs: React.RefObject<HTMLDivElement>[] = []
	private _isUpdatingScale: boolean = false
	static readonly defaultProps = defaultProps

	constructor(props: Props<BlockTypesType>) {
		super(props)

		// generate a random id for each column
		// create all the column starting states and refs
		const stateConstructor: Partial<State<BlockTypesType>> = {}
		this._stateUpdateForNewProps(stateConstructor, props)
		

		this.state = {
			scale: this.props.scale,
			mouseDownIx: null,
			columnNames: [], // this will get overwritten by stateConstructor
			mouseEnterIx: null,
			blockType: 0,
			colWidth: 0,
			details: null,
			...stateConstructor
		}
	}

	static rawBlocksToDataBlocks<BlockTypesType>(
		blocks: any[],
		encoder: (block: any) => IBlockData<BlockTypesType>
	): IBlockData<BlockTypesType>[] {
		return blocks.map(encoder)
	}

	componentDidUpdate(prevProps: Props<BlockTypesType> & UxGridFrameScrollState) {
		const { queryStatus: prevQueryStatus } = prevProps
		const { queryStatus, scrollTop, scrollLeft, blockTypes } = this.props

		if (queryStatus === QueryStatus.PROGRESS && queryStatus !== prevQueryStatus) {
			this._onSubmit()
		}

		if (prevProps.scrollTop !== scrollTop || prevProps.scrollLeft !== scrollLeft) this._handleScroll()

		if (prevProps.blockTypes !== blockTypes) {
			const stateUpdate: Partial<State<BlockTypesType>> = {}
			this._stateUpdateForNewBlockTypes(stateUpdate, prevProps.blockTypes)

			this.setState(stateUpdate)
		}
	}

	_stateUpdateForNewProps = (stateUpdate: Partial<State<BlockTypesType>>, props: Props<BlockTypesType>) => {
		const columnNames: string[] = []
		const columnIds: number[] = []
		props.columns.forEach((column: string | Function, ix: number) => {
			// generate random id number
			const random = randomInteger()

			// create the state keys
			const active = `${random}.active`
			const blocks = `${random}.blocks`
			const editingBlock = `${random}.editingBlock`
			const columnInitialBlocks = props.initialBlocks ? props.initialBlocks[ix] || [] : []

			// set all the state values
			stateUpdate[active] = false
			stateUpdate[blocks] = UxGrid.rawBlocksToDataBlocks(columnInitialBlocks, props.dataToBlock)
			stateUpdate[editingBlock] = undefined

			columnIds.push(random)

			if (typeof column === 'string') {
				columnNames.push(column)
			} else {
				columnNames.push(column(0))
			}
		})

		this._columnIds = columnIds
		stateUpdate.columnNames = columnNames
	}

	_stateUpdateForNewBlockTypes = (stateUpdate: Partial<State<BlockTypesType>>, prevBlockTypes: IBlockType<BlockTypesType>[]) => {
		const {blockTypes} = this.props
		const blockTypeLabels = blockTypes.map(bt => bt.label)

		const removedBlockTypes = prevBlockTypes.filter(bt => !blockTypeLabels.includes(bt.label))

		this._keysForEachColumn("blocks").forEach(bk => {
			const blocks = this.state[bk] as IBlockData<BlockTypesType>[]

			if (blocks) {
				removedBlockTypes.forEach(rbt => {
					blocks.forEach(blockData => {
						const newBlockTypes = blockData.removeBlockType(rbt)

						if (newBlockTypes.length === 0) blockData.status = BlockDataStatus.DELETE
					})
				})
			}

			stateUpdate[bk] = [...blocks]
		})
	}

	dataBlocksToRawBlocks = (
		blocks: IBlockData<BlockTypesType>[],
		decoder: (block: IBlockData<BlockTypesType>) => any
	): any[] => {
		return blocks.map(decoder)
	}

	_getAllBlocks = (): IBlockData<BlockTypesType>[][] => {
		const allBlocks: IBlockData<BlockTypesType>[][] = []

		this._forEachColumnWithState((columnId: number) => {
			const columnIx = this._columnIds.indexOf(columnId)
			const blockKey = this._blockStateKeyForColumn(columnId)
			const blocks = this.state[blockKey] as BlockData<BlockTypesType>[]

			allBlocks[columnIx] = blocks
		}, undefined)

		return allBlocks
	}

	_convertCurrentBlocks = () => {
		const allBlocks = this._getAllBlocks()

		return allBlocks.map((blocks: IBlockData<BlockTypesType>[]) =>
			this.dataBlocksToRawBlocks(blocks, this.props.blockToData)
		)
	}

	_onSubmit = (): void => {
		this.props.handleSave(this._convertCurrentBlocks())
	}

	/**
	 * sets columns to active or inactive
	 */
	toggleColumn = (ix: number) => {
		const columnId = this._columnIds[ix]
		const key = `${columnId}.active`
		const value = !this.state[key]

		this.setState({ [key]: value })
	}

	/**
	 * auto activates a column when an action is taken inside of it
	 */
	_stateForAutoSelectColumn = (ix: number) => {
		const stateUpdate: Partial<State<BlockTypesType>> = {}
		const selectedId = this._columnIds[ix]

		this.forEachActiveColumn((columnId: number) => {
			const activeStateKey = this._activeStateKeyForColumn(columnId)

			if (columnId == selectedId) return
			else {
				stateUpdate[activeStateKey] = false
			}
		})

		this.forEachInactiveColumn((columnId: number) => {
			const activeStateKey = this._activeStateKeyForColumn(columnId)

			if (columnId == selectedId) stateUpdate[activeStateKey] = true
		})

		return stateUpdate
	}

	/**
	 * allows children to update the Grid's mouseDown state
	 */
	_setGridMouseDown = (mouseDownIx: number): void => {
		const stateUpdate: Partial<State<BlockTypesType>> = {
			mouseDownIx
		}

		if (mouseDownIx === null) {
			this._clearColumnEditingBlocks(stateUpdate)
			this._clearMouseState(stateUpdate)
		}

		this.setState(stateUpdate as State<BlockTypesType>)
	}

	_clearMouseState = (stateUpdate: Partial<State<BlockTypesType>>) => {
		return Object.assign(stateUpdate, { mouseDownIx: null, mouseEnterIx: null })
	}

	/**
	 * returns the block label for the current selected block type
	 */
	_currentSelectedBlockTypes = (): IBlockType<BlockTypesType>[] => {
		const { selectedTypes } = this.props

		return selectedTypes
	}

	/**
	 * exposes the active method property to children
	 */
	_setActiveMethod = (setTo?: 'add' | 'modify') => {
		this._activeMethod = setTo === 'add' ? this.addBlockToState : setTo === 'modify' ? this.modifyBlock : undefined
	}

	/**
	 *
	 */
	_callActiveMethod = (indexes: number[], colIx: number, blockToModify?: IBlockData<BlockTypesType>) => {
		if (this.props.autoActivate == true) {
			const stateUpdate = this._stateForAutoSelectColumn(colIx)

			this.setState(stateUpdate, () => {
				if (this._activeMethod) this._activeMethod(indexes, blockToModify)
			})
		} else if (this._activeMethod) this._activeMethod(indexes, blockToModify)
	}

	/**
	 * generates a new block and addes to state for each active column
	 */
	addBlockToState = (indexes: number[]) => {
		const stateUpdate: Partial<State<BlockTypesType>> = {}

		// update each active column's block and editingBlock states
		this.forEachActiveColumn((columnId: number) => {
			const editingStateKey = this._editingBlockStateKeyForColumn(columnId)
			const blockStateKey = this._blockStateKeyForColumn(columnId)

			const columnBlocks = this.state[blockStateKey] as BlockData<BlockTypesType>[]
			const copy = (deepCopy(columnBlocks) || []) as BlockData<BlockTypesType>[]
			const editingBlock = this.state[editingStateKey] as BlockData<BlockTypesType>
			let newBlock: BlockData<BlockTypesType>

			if (editingBlock) {
				newBlock = deepCopy(editingBlock) as BlockData<BlockTypesType>
				newBlock.indexes = indexes

				const blockIx = columnBlocks.indexOf(editingBlock)
				copy.splice(blockIx, 1, newBlock)
			} else {
				newBlock = new BlockData<BlockTypesType>(indexes, this._currentSelectedBlockTypes())
				copy.push(newBlock)
			}

			copy.sort((a: IBlockData<BlockTypesType>, b: IBlockData<BlockTypesType>) => a.indexes[0] - b.indexes[0])
			stateUpdate[editingStateKey] = newBlock
			stateUpdate[blockStateKey] = copy
		})

		// since index ranges are being sorted, need to compare to the current state
		// to figure out which is the opposite end of the trail that was the last index entered
		stateUpdate.mouseEnterIx = this.state.mouseDownIx === indexes[0] ? indexes[indexes.length - 1] : indexes[0]

		// set the new block state
		this.setState(stateUpdate as State<BlockTypesType>)
	}

	/**
	 * either modifies an existing DataBlock for submission to API on save or removes it from the column's block list if it's a new block
	 */
	modifyBlock = (ixRange: number[], blockToModify: IBlockData<BlockTypesType>): void => {
		// TODO
		// check if a block with status 'DELETE' should be reused if a block is removed and then a similar block is created
		// i.e. change from 'DELETE' to 'PUT'

		if (!blockToModify) {
			console.warn("trying to modify block that doesn't exist")
			return
		}

		// if mouse event comes from a non-active block type, default to adding the active block type (i.e. click through to the bottom ux layer)
		if (blockToModify && hasDifference(blockToModify.types, this.props.selectedTypes)) {
			this.addBlockToState(ixRange)
			return
		}

		const stateUpdate: Partial<State<BlockTypesType>> = {}
		const complexModification =
			ixRange.length === 1 &&
			blockToModify.indexes.length > 2 &&
			ixRange[0] > blockToModify.indexes[0] &&
			ixRange[0] < blockToModify.indexes[blockToModify.indexes.length - 1]

		// remove the block from state since it doesn't exist on the server yet
		this.forEachActiveColumn(
			complexModification ? this._complexBlockModification : this._simpleBlockModification,
			ixRange,
			blockToModify,
			stateUpdate
		)

		// if newIndexes = [], the whole block has been removed and
		// it's time to just reset the mouse state to completely disable Ux
		const newIndexes = blockToModify ? difference(blockToModify.indexes, ixRange) : []

		const newMouseState = {
			mouseDownIx:
				newIndexes.length === 0 ? null : this.state.mouseDownIx ? this.state.mouseDownIx : newIndexes[0],
			mouseEnterIx: newIndexes.length > 0 ? ixRange[ixRange.length - 1] : null
		}

		// set the new block state
		this.setState(prevState => Object.assign({}, prevState, newMouseState, stateUpdate) as State<BlockTypesType>)
	}

	/**
	 * side effect function for modifying an existing block when a user starts in the middle of the block
	 */
	_complexBlockModification = (
		columnId: number,
		ixRange: number[],
		blockToModify: IBlockData<BlockTypesType>,
		stateUpdate: Partial<State<BlockTypesType>>
	) => {
		// get the column's state keys for its blocks and current block being edited
		const editingBlockKey = this._editingBlockStateKeyForColumn(columnId)
		const blocksKey = this._blockStateKeyForColumn(columnId)
		const columnBlocks = this.state[blocksKey] as IBlockData<BlockTypesType>[]
		const currentInstance = this._findCurrentBlockInstance(blockToModify)
		const originalBlockIx = currentInstance ? columnBlocks.indexOf(currentInstance) : -1

		// copy the original state so it can be re-set
		const copy = deepCopy(columnBlocks) as IBlockData<BlockTypesType>[]

		// split into new blocks, set the POST status for each
		const [lowerIndexes, upperIndexes] = bucketLessAndGreaterThan(blockToModify.indexes, ixRange[0])

		const lowerBlock = new BlockData(lowerIndexes, blockToModify.types)
		const upperBlock = new BlockData(upperIndexes, blockToModify.types, {
			initBlockGroupState: {
				ixDown: ixRange[0] - lowerIndexes[0]
			}
		})

		// splice the original block if it was a 'POST' status
		if (blockToModify.status === BlockDataStatus.POST) {
			copy.splice(originalBlockIx, 1, lowerBlock, upperBlock)
		} else {
			// otherwise set status to DELETE and update indexes to [] on the original block if it is from server
			copy[originalBlockIx].indexes = []
			copy.push(lowerBlock)
			copy.push(upperBlock)
		}
		// log the alternate editing blocks incase the user goes the other way

		copy.sort((a: IBlockData<BlockTypesType>, b: IBlockData<BlockTypesType>) => a.indexes[0] - b.indexes[0])
		stateUpdate[editingBlockKey] = lowerBlock
		stateUpdate[blocksKey] = copy
	}

	/**
	 * side effect function for modifying an existing block when a user clicks at the start or end
	 */
	_simpleBlockModification = (
		columnId: number,
		ixRange: number[],
		blockToModify: IBlockData<BlockTypesType>,
		stateUpdate: Partial<State<BlockTypesType>>
	) => {
		const { selectedTypes } = this.props
		const editingBlockKey = this._editingBlockStateKeyForColumn(columnId)
		const columnBlocksKey = this._blockStateKeyForColumn(columnId)
		const columnBlocks = this.state[columnBlocksKey] as BlockData<BlockTypesType>[]
		const editingBlock = this.state[editingBlockKey] as BlockData<BlockTypesType>
		const newIndexes = blockToModify ? difference(blockToModify.indexes, ixRange) : []
		const copy = deepCopy(columnBlocks) as BlockData<BlockTypesType>[]

		if (editingBlock) {
			// can't use the object ref itself since it's being spliced out
			// for state updates, at some point it gets lost
			const allIndexes = columnBlocks.map(block => block.indexes)
			const editingBlockIx = indexOf(
				allIndexes,
				(value: number[]) =>
					isEqual(value, blockToModify.indexes) && !hasDifference(blockToModify.types, selectedTypes)
			)

			if (editingBlock.status === BlockDataStatus.POST && newIndexes.length === 0) {
				copy.splice(editingBlockIx, 1)
			} else {
				copy[editingBlockIx].indexes = newIndexes
			}
		} else {
			// have not searched for the matching blocks yet
			// so pull all the references for any later modifications
			columnBlocks.forEach((block: IBlockData<BlockTypesType>, blockIx: number) => {
				if (
					blockToModify === block ||
					(isEqual(block.indexes, blockToModify.indexes) && !hasDifference(block.types, blockToModify.types))
				) {
					if (block.status === BlockDataStatus.POST && newIndexes.length === 0) {
						copy.splice(blockIx, 1)
					} else {
						copy[blockIx].indexes = newIndexes

						stateUpdate[editingBlockKey] = copy[blockIx]
					}
				}
			})
		}

		copy.sort((a: IBlockData<BlockTypesType>, b: IBlockData<BlockTypesType>) => a.indexes[0] - b.indexes[0])
		stateUpdate[columnBlocksKey] = copy

		if (newIndexes.length === 0) {
			stateUpdate[editingBlockKey] = undefined
		}
	}

	/**
	 * applies the provided function to each active / selected column
	 */
	forEachActiveColumn = (doThis: (columnName: number, ...args: any[]) => void, ...callBackArgs: any[]) =>
		this._forEachColumnWithState(doThis, true, ...callBackArgs)

	/**
	 * applies the provided function to each inactive / unselected column
	 */
	forEachInactiveColumn = (doThis: (columnName: number, ...args: any[]) => void, ...callBackArgs: any[]) =>
		this._forEachColumnWithState(doThis, false, ...callBackArgs)

	/**
	 * core method for the partially applied forEachActiveColumn and forEachInactiveColumn methods
	 * passes any additional arguments along to the callback function
	 */
	private _forEachColumnWithState = (
		doThis: (columnName: number, ...args: any[]) => void,
		checkFor: boolean | undefined,
		...callBackArgs: any[]
	) => {
		// const { columns } = this.props
		const activeColumns = this._getColumnActiveStates()

		// loop over and only act on the desired columns
		// if checkFor is undefined, then assume all columns should have the action taken
		activeColumns.forEach((isActive: boolean, ix: number) => {
			if (checkFor === undefined) doThis(this._columnIds[ix], ...callBackArgs)
			else if (isActive === checkFor) {
				doThis(this._columnIds[ix], ...callBackArgs)
			}
		})
	}

	/**
	 * returns an array of active states in the same order of columns prop
	 */
	_getColumnActiveStates = () => {
		const keys = this._columnActiveKeys()

		return keys.map(k => this.state[k] as boolean)
	}

	/**
	 * side effect method to apply cleared editing blocks to a state update
	 */
	_clearColumnEditingBlocks = (stateUpdate: Partial<State<BlockTypesType>>) => {
		const keys = this._columnEditingBlockKeys()

		keys.reduce((acc: Partial<State<BlockTypesType>>, key: string) => {
			acc[key] = undefined

			return acc
		}, stateUpdate)
	}

	// *****
	// Column State Key / Value Getters
	// *****
	_columnActiveKeys = () => {
		return this._keysForEachColumn('active')
	}

	_columnBlockKeys = () => {
		return this._keysForEachColumn('blocks')
	}

	_columnEditingBlockKeys = () => {
		return this._keysForEachColumn('editingBlock')
	}

	private _keysForEachColumn = (type: 'active' | 'blocks' | 'editingBlock') => {
		const functions = {
			active: this._activeStateKeyForColumn,
			blocks: this._blockStateKeyForColumn,
			editingBlock: this._editingBlockStateKeyForColumn
		}

		return this._columnIds.map((columnId: number) => functions[type](columnId))
	}

	// private _columnIdFromName = (columnName: string) => {
	// 	return this._columnIds[columnName]
	// }

	private _columnNameFromId = (columnId: number): string => {
		const ix = this._columnIds.indexOf(columnId)

		if (ix > -1) return this.state.columnNames[ix]
		else return ''
	}

	private _activeStateKeyForColumn = (columnId: number) => {
		return `${columnId}.active`
	}

	private _blockStateKeyForColumn = (columnId: number) => {
		return `${columnId}.blocks`
	}

	private _editingBlockStateKeyForColumn = (columnId: number) => {
		return `${columnId}.editingBlock`
	}

	private _handleScroll() {
		let wasVerticalStart = false
		const { scrollRef, scrollHeight } = this._getScrollState()
		const { scale } = this.state

		const stateUpdate: Partial<State<BlockTypesType>> = {}

		if (this._isAtVerticalEnd()) {
			this._handleReachBottom(stateUpdate)
		} else if (this._isAtVerticalStart()) {
			if (scrollRef) {
				this._handleReachTop(stateUpdate)
				wasVerticalStart = true
			}
		} else {
			this._updateColumnNames(stateUpdate)
		}

		// need to calc blockHeight here otherwise the scale will have updated, but scrollHeight won't
		const blockHeight = scrollHeight ? scrollHeight / scale.totalIncrements : null

		if (Object.keys(stateUpdate).length > 0) {
			this.setState(stateUpdate as State<BlockTypesType>, () => {
				if (wasVerticalStart) this._autoScroll(blockHeight)
				if (this._isUpdatingScale) this._isUpdatingScale = false
			})
		}
	}

	private _autoScroll = (blockHeight: number | null) => {
		const { scrollTo, scrollTop } = this._getScrollState()
		const newScrollTop: number | null = blockHeight ? blockHeight * this._originalScale.totalIncrements : null

		if (scrollTo && newScrollTop && newScrollTop !== scrollTop) {
			scrollTo({ scrollTop: newScrollTop })
		}
	}

	private _isAtVerticalStart = (): boolean => {
		const { scrollTop } = this._getScrollState()

		if (scrollTop! <= 25) return true

		return false
	}

	private _isAtVerticalEnd = (): boolean => {
		const { scrollHeight, scrollTop, offsetHeight } = this._getScrollState()

		if (scrollTop! + offsetHeight! >= scrollHeight!) return true

		return false
	}

	private _getScrollState = () => {
		const {
			scrollHeight,
			scrollWidth,
			scrollTop,
			scrollLeft,
			offsetHeight,
			offsetWidth,
			scrollRef,
			scrollTo
		} = this.props
		return { scrollHeight, scrollWidth, scrollTop, scrollLeft, offsetHeight, offsetWidth, scrollRef, scrollTo }
	}

	private _handleReachBottom = (stateUpdate: Partial<State<BlockTypesType>>) =>
		this._handleReachVerticalEnd('bottom', stateUpdate)
	private _handleReachTop = (stateUpdate: Partial<State<BlockTypesType>>) =>
		this._handleReachVerticalEnd('top', stateUpdate)
	private _handleReachVerticalEnd = (direction: 'top' | 'bottom', stateUpdate: Partial<State<BlockTypesType>>) => {
		if (!this._isUpdatingScale) {
			this._isUpdatingScale = true
			const { infiniteScroll } = this.props

			// infinite y is true
			if (infiniteScroll[1]) {
				const { scale } = this.state
				const newScale = new ScalePrototype(scale.min, scale.max, scale.increment)

				if (direction === 'top') {
					// const curMin = scale.min
					// const diffInMin = Math.abs(curMin - this._originalScale.min) / this._originalScale.totalIncrements
					newScale.expandNegative(this._originalScale.totalIncrements / scale.totalIncrements)
				} else if (direction === 'bottom') {
					// const curMax = scale.max
					// const diffInMax = (curMax - this._originalScale.max) / this._originalScale.totalIncrements
					newScale.expandPositive(this._originalScale.totalIncrements / scale.totalIncrements)
				}

				stateUpdate.scale = newScale

				this._updateColumnNames(stateUpdate, newScale)
			}
		}
	}

	private _updateColumnNames = (stateUpdate: Partial<State<BlockTypesType>>, newScale?: IScaleData) => {
		const forScale = newScale || this.state.scale

		const calcedBlockIx = this._blockIxFromScrollTop(forScale)
		if (calcedBlockIx !== null) {
			const newColumnNames: string[] = []

			this.props.columns.forEach((c: string | Function) => {
				if (typeof c === 'string') {
					newColumnNames.push(c)
				} else {
					newColumnNames.push(c(calcedBlockIx, this.state.scale))
				}
			})

			if (hasDifference(newColumnNames, this.state.columnNames)) stateUpdate.columnNames = newColumnNames
		}
	}

	private _blockIxFromScrollTop = (scale?: IScaleData): number | null => {
		const { scrollTop, scrollHeight } = this._getScrollState()
		const useScale = scale ? scale : this.state.scale

		if (scrollTop && scrollHeight) {
			const result = Math.floor(scrollTop / scrollHeight * useScale.totalIncrements) + useScale.min
			return result
		}

		return null
	}

	private _showDetails = (block: IBlockData<BlockTypesType>) => {
		this.setState({ details: block })
	}

	private _closeDetails = () => {
		this.setState({ details: null })
	}

	private _findCurrentBlockInstance = (
		searchFor: IBlockData<BlockTypesType>
	): IBlockData<BlockTypesType> | undefined => {
		const blockKeys = this._columnBlockKeys()

		for (let i = 0, n = blockKeys.length; i < n; i++) {
			const blockKey = blockKeys[i]
			const blocks = this.state[blockKey] as IBlockData<BlockTypesType>[]

			const block = blocks.find(b => b.id === searchFor.id)

			if (block) {
				return block
			}
		}

		return
	}

	private _getOverlay = () => {
		const {details} = this.state

		if (details) {
			const { DetailsView, blockTypes, multiTypeSelect, labelConversion} = this.props

			return <Overlay<BlockTypesType>
						multiTypeSelect={multiTypeSelect}
						block={details}
						blockTypes={blockTypes}
						handleClose={this._closeDetails}
						Content={DetailsView}
						findCurrentBlockInstance={this._findCurrentBlockInstance}
						labelConversion={labelConversion}
					/>
		} else return undefined
	}

	render() {
		const { mouseDownIx, mouseEnterIx, scale, details } = this.state
		const {
			autoActivate,
			labelFrequency,
			labelConversion,
			selectedTypes,
			className
		} = this.props

		return (
			<React.Fragment>
				{this._getOverlay()}
				<FlexBox inline container className={`${styles.uxGridWrapper} ${className}`}>
					<Scale dataScale={scale} labelFrequency={labelFrequency} labelConverter={labelConversion} />
					{this._columnIds.map((columnId: number, ix: number) => {
						const activeKey = this._activeStateKeyForColumn(columnId)
						const blocksKey = this._blockStateKeyForColumn(columnId)

						return (
							<Column
								key={ix}
								autoActivate={autoActivate}
								isActive={
									!(this.props.queryStatus === QueryStatus.PROGRESS) &&
									(autoActivate || (this.state[activeKey] as boolean))
								}
								ix={ix}
								title={this._columnNameFromId(columnId)}
								scale={scale}
								selectedBlockTypes={selectedTypes}
								blocks={this.state[blocksKey] as BlockData<BlockTypesType>[]}
								detailViewBlock={details}
								gridMouseDownIx={mouseDownIx}
								gridMouseEnterIx={mouseEnterIx}
								toggleActive={this.toggleColumn}
								setActiveMethod={this._setActiveMethod}
								callActiveMethod={this._callActiveMethod}
								showDetails={this._showDetails}
								gridMouseDown={this._setGridMouseDown}
								labelConversion={labelConversion}
								style={{ width: `calc((100% - 42px - 208px) / ${this._columnIds.length} )` }}
							/>
						)
					})}
				</FlexBox>
			</React.Fragment>
		)
	}
}

const UxGridWithScroll = withScroll(UxGrid)

class UxGridWithTypeSelect<T extends object> extends React.PureComponent<
	Pick<Props<T>, Exclude<keyof Props<T>, 'selectedTypes'>> & BlockTypeSelectProps<T>,
	BlockTypeSelectState
> {
	state = {
		selected: ['0'] as string[]
	}

	/**
	 * switches the selected block type
	 */
	private _onBlockTypeSelect = (ix: string) => {
		const { selected } = this.state
		const stateUpdate: BlockTypeSelectState = {
			selected
		}

		if (this.props.multiTypeSelect) {
			const selectedIx = selected.indexOf(ix)

			const copy = selected.splice(0)
			// remove the ix from the selected list
			if (selectedIx > -1  && copy.length > 0) {
				copy.splice(selectedIx, 1)
			} else {
				// add the ix to the list
				copy.push(ix)
			}

			stateUpdate.selected = copy
		} else {

			stateUpdate.selected = [ix]
		}

		this.setState(stateUpdate)
	}

	render() {
		const { selected } = this.state
		const { blockTypes, multiTypeSelect, withBlockTypeManagement, AddBlockTypeComponent, removeBlockType } = this.props
		const selectedTypes: IBlockType<T>[] = blockTypes.reduce((acc: IBlockType<T>[], cur, ix) => {
			if (selected.includes(ix.toString())) {
				acc.push(cur)
			}

			return acc
		}, [])

		return (
			<div className={styles.root}>
				<div className={styles.scaleFade} />
				<UxGridWithScroll selectedTypes={selectedTypes} {...this.props} />
				{(blockTypes.length > 1 || withBlockTypeManagement) && (
					<BlockTypeSelector<T> 
						withManagement={withBlockTypeManagement} 
						multiSelect={multiTypeSelect} 
						selected={selected} 
						blockTypes={blockTypes} 
						onSelect={this._onBlockTypeSelect} 
						AddBlockTypeComponent={AddBlockTypeComponent} 
						removeBlockType={removeBlockType} 
					/>
				)}
			</div>
		)
	}
}

const UxGridWithTheme = <T extends object>(props: Pick<Props<T>, Exclude<keyof Props<T>, 'selectedTypes'>> & BlockTypeSelectProps<T>) => {
	return (
		<ThemeProvider theme={theme}>
			<UxGridWithTypeSelect {...props} />
		</ThemeProvider>
	)
}

export default UxGridWithTheme
